// ============================================
// APPLICATION - Usuarios Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usuariosRepository } from '../../infrastructure/http/usuarios.repository';
import { queryKeys } from '../query-keys';
import type { UsuarioSucursal, CrearUsuarioDto, ActualizarUsuarioDto } from '../../domain/dtos';

/**
 * Hook para obtener todos los usuarios de todas las sucursales
 */
export const useUsuarios = () => {
  return useQuery({
    queryKey: queryKeys.usuarios.all,
    queryFn: () => usuariosRepository.obtenerTodosUsuarios(),
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 2 * 60 * 1000, // 2 minutos
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para obtener un usuario por ID con detalles completos
 */
export const useUsuarioPorId = (usuarioId: number | null) => {
  return useQuery({
    queryKey: ['usuario', usuarioId],
    queryFn: () => usuariosRepository.obtenerUsuarioPorId(usuarioId!),
    enabled: !!usuarioId,
    gcTime: 5 * 60 * 1000, // 5 minutos
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: 1,
    throwOnError: false,
  });
};

/**
 * Hook para crear un nuevo usuario
 */
export const useCrearUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (datos: CrearUsuarioDto) => usuariosRepository.crearUsuario(datos),
    onSuccess: (nuevoUsuario) => {
      // Actualizar cache directamente agregando el nuevo usuario
      queryClient.setQueryData<UsuarioSucursal[]>(queryKeys.usuarios.all, (old) => {
        if (!old) return [nuevoUsuario];
        return [...old, nuevoUsuario];
      });
    },
  });
};

/**
 * Hook para actualizar un usuario
 */
export const useActualizarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (datos: ActualizarUsuarioDto) => usuariosRepository.actualizarUsuario(datos),
    onSuccess: (usuarioActualizado) => {
      // Actualizar cache reemplazando el usuario actualizado
      queryClient.setQueryData<UsuarioSucursal[]>(queryKeys.usuarios.all, (old) => {
        if (!old) return [];
        return old.map((usuario) =>
          usuario.id === usuarioActualizado.id ? usuarioActualizado : usuario
        );
      });
    },
  });
};

/**
 * Hook para eliminar un usuario (marca como inactivo)
 */
export const useEliminarUsuario = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (usuarioId: number) => usuariosRepository.eliminarUsuario(usuarioId),
    onSuccess: (_, usuarioId) => {
      // Actualizar cache marcando el usuario como inactivo en lugar de eliminarlo
      queryClient.setQueryData<UsuarioSucursal[]>(queryKeys.usuarios.all, (old) => {
        if (!old) return [];
        return old.map((usuario) =>
          usuario.id === usuarioId ? { ...usuario, activo: false } : usuario
        );
      });
    },
  });
};
